import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { cartApiService } from '../../services/cartApiService';

// Async thunks - Migrated to Backend Custom API
export const fetchCartItems = createAsyncThunk(
  'cart/fetchCartItems',
  async (_, { rejectWithValue }) => {
    try {
      const { success, data, error } = await cartApiService.getCart();
      if (!success) throw new Error(error || 'Failed to fetch cart');
      return data?.items || [];
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const addToCartAsync = createAsyncThunk(
  'cart/addToCartAsync',
  async ({ artwork_id, quantity = 1 }, { rejectWithValue }) => {
    try {
      const { success, data, error } = await cartApiService.addItemToCart(artwork_id, quantity);
      if (!success) throw new Error(error || 'Failed to add item to cart');
      return data?.cart?.items || [];
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateCartItemAsync = createAsyncThunk(
  'cart/updateCartItemAsync',
  async ({ artwork_id, quantity }, { rejectWithValue }) => {
    try {
      const { success, data, error } = await cartApiService.updateCartItem(artwork_id, quantity);
      if (!success) throw new Error(error || 'Failed to update cart item');
      return data?.cart?.items || [];
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const removeFromCartAsync = createAsyncThunk(
  'cart/removeFromCartAsync',
  async ({ artwork_id }, { rejectWithValue }) => {
    try {
      const { success, data, error } = await cartApiService.removeCartItem(artwork_id);
      if (!success) throw new Error(error || 'Failed to remove cart item');
      return data?.cart?.items || [];
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const clearCartAsync = createAsyncThunk(
  'cart/clearCartAsync',
  async (_, { rejectWithValue }) => {
    try {
      const { success, error } = await cartApiService.clearCart();
      if (!success) throw new Error(error || 'Failed to clear cart');
      return [];
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const syncCartAsync = createAsyncThunk(
  'cart/syncCartAsync',
  async ({ local_items = [] }, { rejectWithValue }) => {
    try {
      const { success, data, error } = await cartApiService.syncCart(local_items);
      if (!success) throw new Error(error || 'Failed to sync cart');
      return data?.cart?.items || [];
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const calculateTotal = (items) => {
  return items.reduce((sum, item) => {
    // Field mapping middleware ensures consistent field names
    const price = item.artwork?.price || 0;
    const quantity = item.quantity || 1;
    return sum + (price * quantity);
  }, 0);
};

const initialState = {
  items: [],
  total: 0,
  loading: false,
  syncing: false,
  merging: false,
  updating_items: [], // Track which items are being updated
  error: null
};

export const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    // Local-only actions for offline functionality
    addToCartLocal: (state, action) => {
      const { id, quantity = 1 } = action.payload;
      // Use consistent artwork_id field naming
      const existingItem = state.items.find(item => 
        item.artwork?.id === id || item.artwork_id === id
      );
      
      if (existingItem) {
        existingItem.quantity += quantity;
      } else {
        state.items.push({ ...action.payload, quantity });
      }
      
      state.total = calculateTotal(state.items);
    },
    removeFromCartLocal: (state, action) => {
      const { id } = action.payload;
      state.items = state.items.filter(item =>
        item.artwork?.id !== id && item.artwork_id !== id && item.id !== id
      );
      state.total = calculateTotal(state.items);
    },
    updateQuantityLocal: (state, action) => {
      const { id, quantity } = action.payload;
      // Use consistent field naming
      const item = state.items.find(item => 
        item.artwork?.id === id || item.artwork_id === id
      );
      if (item) {
        item.quantity = quantity;
        state.total = calculateTotal(state.items);
      }
    },
    
    // Optimistic update actions
    updateQuantityOptimistic: (state, action) => {
      const { id, quantity } = action.payload;
      
      // Add to updating items if not already there
      if (!state.updating_items.includes(id)) {
        state.updating_items.push(id);
      }
      
      // Update quantity immediately (optimistic)
      const item = state.items.find(item => 
        item.artwork?.id === id || item.artwork_id === id
      );
      if (item) {
        item.quantity = quantity;
        state.total = calculateTotal(state.items);
      }
    },
    
    updateQuantitySuccess: (state, action) => {
      const { id } = action.payload;

      // Remove from updating items
      state.updating_items = state.updating_items.filter(item_id => item_id !== id);
    },

    updateQuantityFailure: (state, action) => {
      const { id, original_quantity } = action.payload;

      // Revert optimistic update
      const item = state.items.find(item =>
        item.artwork?.id === id || item.artwork_id === id
      );
      if (item) {
        item.quantity = original_quantity;
        state.total = calculateTotal(state.items);
      }

      // Remove from updating items
      state.updating_items = state.updating_items.filter(item_id => item_id !== id);
    },
    clearError: (state) => {
      state.error = null;
    },
    clearCart: (state) => {
      state.items = [];
      state.total = 0;
      state.loading = false;
      state.error = null;
    },
    
    // New hydration and sync actions for persistence
    hydrateCartFromStorage: (state, action) => {
      // Load cart from localStorage on app startup
      const localItems = action.payload || [];
      state.items = localItems;
      state.total = calculateTotal(localItems);
      
      if (import.meta.env.DEV) {
        console.log('💧 [CartSlice] Hydrated cart from storage:', localItems.length, 'items');
      }
    },
    
    syncCartToDB: (state) => {
      // Mark that cart is being synced to database
      state.syncing = true;
      state.error = null;
    },
    
    syncCartToDBSuccess: (state, action) => {
      // Cart successfully synced to database
      state.syncing = false;
      state.items = action.payload || state.items;
      state.total = calculateTotal(state.items);
      
      if (import.meta.env.DEV) {
        console.log('✅ [CartSlice] Cart synced to database successfully');
      }
    },
    
    syncCartToDBError: (state, action) => {
      // Cart sync to database failed
      state.syncing = false;
      state.error = action.payload;

      if (import.meta.env.DEV) {
        console.error('❌ [CartSlice] Cart sync to database failed:', action.payload);
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch cart items
      .addCase(fetchCartItems.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCartItems.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
        state.total = calculateTotal(action.payload);
      })
      .addCase(fetchCartItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Add to cart
      .addCase(addToCartAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addToCartAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
        state.total = calculateTotal(action.payload);
      })
      .addCase(addToCartAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update cart item (optimistic - no global loading)
      .addCase(updateCartItemAsync.pending, (state) => {
        // Don't set global loading for updates
        state.error = null;
      })
      .addCase(updateCartItemAsync.fulfilled, (state, action) => {
        // Update completed successfully, refresh full cart data
        state.items = action.payload;
        state.total = calculateTotal(action.payload);
      })
      .addCase(updateCartItemAsync.rejected, (state, action) => {
        state.error = action.payload;
      })
      
      // Remove from cart
      .addCase(removeFromCartAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeFromCartAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
        state.total = calculateTotal(action.payload);
      })
      .addCase(removeFromCartAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Clear cart
      .addCase(clearCartAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(clearCartAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
        state.total = 0;
      })
      .addCase(clearCartAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Sync cart (new thunk for Backend Custom API)
      .addCase(syncCartAsync.pending, (state) => {
        state.syncing = true;
        state.error = null;
      })
      .addCase(syncCartAsync.fulfilled, (state, action) => {
        state.syncing = false;
        state.items = action.payload;
        state.total = calculateTotal(action.payload);
      })
      .addCase(syncCartAsync.rejected, (state, action) => {
        state.syncing = false;
        state.error = action.payload;
      });
  }
});

export const {
  addToCartLocal,
  removeFromCartLocal,
  updateQuantityLocal,
  updateQuantityOptimistic,
  updateQuantitySuccess,
  updateQuantityFailure,
  clearError,
  clearCart,
  hydrateCartFromStorage,
  syncCartToDB,
  syncCartToDBSuccess,
  syncCartToDBError
} = cartSlice.actions;

export default cartSlice.reducer;